import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  CircularProgress,
} from '@material-ui/core';
import {
  AssessmentOutlined as AssessmentIcon,
  TrendingUpOutlined as TrendingUpIcon,
  SpeedOutlined as SpeedIcon,
} from '@material-ui/icons';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useMetricsStyles } from './styles/metrics.styles';
import { WebsiteMetric } from './types';
import { UrlHelper } from '../../utils/url-helper';
import { 
  QUERY_KEYS, 
  CACHE_TIMES 
} from './constants';
import { WebsiteViewService } from 'src/model/WebsiteViewService';
import { Metric, MetricsList } from '@amzn/swo-unified-dashboard-service-lambda-js-client/lib/smswounifieddashboardservicelambda';
import { millisecondsToTimePeriod } from 'src/utils/time-helper';

export const WebsiteMetrics = React.memo((): JSX.Element => {
  const classes = useMetricsStyles();
  const location = useLocation();
  const website = UrlHelper.getParamValue('website');

  // React Query to fetch metrics for the website
  const { data, isFetching: isLoading, error } = useQuery({
    queryKey: [QUERY_KEYS.WEBSITE_METADATA, website, 'metrics'],
    queryFn: () => new WebsiteViewService().getWebsiteMetrics(website || ''),
    enabled: !!website,
    staleTime: CACHE_TIMES.STALE_TIME,
    cacheTime: CACHE_TIMES.CACHE_TIME,
    retry: 3
  });

  const renderMetricItem = (item: Metric & { color?: string }, index: number) => {
    const formatTimePeriod = (milliseconds: number): string => {
    if (!isFinite(milliseconds)) return 'N/A';
    
    const { days, hours } = millisecondsToTimePeriod(milliseconds);
    
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    
    return parts.length > 0 ? parts.join(' ') : '0h';
    };

    const metricValue = item.value.toString().toLowerCase() === 'infinity' ? 'N/A' : formatTimePeriod(item.value)
    return (
    <Grid item xs={12} key={index}>
      <Paper className={classes.metricItemPaper} elevation={1}>
        <Box className={classes.metricItem}>
          <Box className={classes.iconContainer}>
            {React.cloneElement(<SpeedIcon />, { 
              fontSize: 'large',
              style: { color: item.color || '#2196F3' }
            })}
          </Box>
          <Box className={classes.metricContent}>
            <Typography variant="caption" className={classes.fieldLabel}>
              {item.name}
            </Typography>
            <Typography variant="h6" className={classes.fieldValue} style={{fontSize: '1.2rem'}}>
              {metricValue}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Grid>
  )};

  // Show loading state
  if (isLoading) {
    return (
      <Paper className={classes.paper}>
        <Box className={classes.header}>
          <Box className={classes.headerContent}>
            <AssessmentIcon className={classes.headerIcon} />
            <Typography variant="h4" className={classes.headerTitle}>
              Website Metrics
            </Typography>
          </Box>
        </Box>
        <Box className={classes.content}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </Box>
      </Paper>
    );
  }

  // Show error state
  if (error) {
    return (
      <Paper className={classes.paper}>
        <Box className={classes.header}>
          <Box className={classes.headerContent}>
            <AssessmentIcon className={classes.headerIcon} />
            <Typography variant="h4" className={classes.headerTitle}>
              Website Metrics
            </Typography>
          </Box>
        </Box>
        <Box className={classes.content}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography color="textSecondary">
              {!website ? 'No website selected' : 'Failed to load metrics'}
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      {/* Header - Timeline Style */}
      <Box className={classes.header}>
        <Box className={classes.headerContent}>
          <AssessmentIcon className={classes.headerIcon} />
          <Typography variant="h4" className={classes.headerTitle}>
            Website Metrics
          </Typography>
        </Box>
      </Box>

      {/* Content - Compact Layout */}
      <Box className={classes.content}>
        <Grid container spacing={2}>
          {data?.metrics?.map((item: Metric & { color?: string }, index: number) => 
            renderMetricItem(item, index)
          )}
        </Grid>
      </Box>
    </Paper>
  );
});

WebsiteMetrics.displayName = 'WebsiteMetrics';
